<script setup lang="ts">
import axios from 'axios';
import { computed, ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { toJpg as toJpgPage } from '@/routes/tools/pdf';
import toJpgTool from '@/routes/tools/pdf/to-jpg';

const { maxMb } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.to_jpg.seo',
    fallbackTitleKey: 'tools.pdf.to_jpg.title',
    fallbackTitleLabel: 'PDF to JPG',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: toJpgPage.url(),
});

const toJpgDropSublabel = computed(
    () =>
        `${t('tools.pdf.ui.one_pdf_file', 'One PDF file')} · ${t('tools.dropzone.upload_limit_max', 'max')} ${maxMb.value} MB · ${t('tools.pdf.ui.to_jpg_drop_extra', 'single page downloads as JPG, multi-page as ZIP')}`,
);

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.to_jpg.title',
                title: 'PDF to JPG',
                href: toJpgPage.url(),
            },
        ],
    },
});

type Quality = 'low' | 'medium' | 'high';
type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const quality = ref<Quality>('medium');
const status = ref<Status>('idle');
const statusMessage = ref('');

const qualityOptions = computed(() => {
    type Opt = { label: string; value: Quality; desc: string };

    const opts: Opt[] = [
        {
            label: t('tools.pdf.ui.quality_low', 'Low'),
            value: 'low',
            desc: t('tools.pdf.ui.quality_dpi_low', '72 DPI'),
        },
        {
            label: t('tools.pdf.ui.quality_medium', 'Medium'),
            value: 'medium',
            desc: t('tools.pdf.ui.quality_dpi_medium', '150 DPI'),
        },
        {
            label: t('tools.pdf.ui.quality_high', 'High'),
            value: 'high',
            desc: t('tools.pdf.ui.quality_dpi_high', '300 DPI'),
        },
    ];

    return opts;
});

function onFileSelected(files: File[]): void {
    file.value = files[0] ?? null;
    status.value = 'idle';
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        const text = await (e.response.data as Blob).text();

        try {
            const parsed = JSON.parse(text);
            const messages = Object.values(parsed.errors ?? {}).flat();

            return (
                (messages[0] as string) ||
                t('tools.pdf.ui.conversion_failed', 'Conversion failed.')
            );
        } catch {
            return t('tools.pdf.ui.conversion_failed', 'Conversion failed.');
        }
    }

    return t('tools.pdf.ui.conversion_failed', 'Conversion failed.');
}

async function convert(): Promise<void> {
    if (!file.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t(
        'tools.pdf.ui.converting_to_jpg',
        'Converting PDF to JPG...',
    );

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('quality', quality.value);

    try {
        const response = await axios.post(toJpgTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const isZip = (response.data as Blob).type === 'application/zip';
        const filename = match?.[1] ?? (isZip ? 'pages.zip' : 'page-1.jpg');

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = isZip
            ? t(
                  'tools.pdf.ui.conversion_complete_zip',
                  'Conversion complete! Downloaded all pages as ZIP.',
              )
            : t('tools.pdf.ui.conversion_complete', 'Conversion complete!');
        file.value = null;
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-to-jpg" />

    <ToolLayout
        :title="t('tools.pdf.to_jpg.title', 'PDF to JPG')"
        :subtitle="t('tools.pdf.to_jpg.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    accept="application/pdf"
                    :label="
                        t('tools.pdf.ui.drop_pdf', 'Click or drag a PDF here')
                    "
                    :sublabel="toJpgDropSublabel"
                    @update:model-value="onFileSelected"
                />
                <div v-if="file" class="mt-3">
                    <FileCard :file="file" @remove="file = null" />
                </div>
            </div>

            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <p
                    class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                    {{ t('tools.pdf.ui.output_quality', 'Output Quality') }}
                </p>
                <div class="flex gap-2">
                    <button
                        v-for="opt in qualityOptions"
                        :key="opt.value"
                        type="button"
                        class="flex-1 rounded-lg border px-4 py-3 text-sm font-medium transition-colors"
                        :class="
                            quality === opt.value
                                ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-300'
                                : 'border-border bg-card text-muted-foreground hover:border-brand-300 dark:text-foreground/85'
                        "
                        @click="quality = opt.value"
                    >
                        <span class="block">{{ opt.label }}</span>
                        <span
                            class="text-xs text-gray-400 dark:text-gray-500"
                            >{{ opt.desc }}</span
                        >
                    </button>
                </div>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!file || status === 'loading'"
                @click="convert"
            >
                {{ t('tools.pdf.ui.convert_to_jpg', 'Convert to JPG') }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
